import React, { memo, useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useThrottleFn } from 'ahooks';
// @ts-ignore
import { isEqual } from 'lodash-es';
import BaseEchart from '../BaseEchart';
import useOptions from './hooks/useOptions';
import useOptionsExtend from './hooks/useOptionsExtend';
import { seriesNameFormatter } from '../formatters';
import './index.scss';
import { useIntl, getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';

import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

const DEFAULT_PROPS: PieEchartPropsTypes = {
  data: null,
  ignoreSortNames: [],
  tooltipPosition: 'right',
  tooltipHeaderRightTitle: '',
  fontSize: 12,
};

const PieEchart: React.ForwardRefRenderFunction<IGlobalViewPieEChartInstance, PieEchartPropsTypes> = (props, ref) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;
  const { data, ignoreSortNames, tooltipPosition, tooltipHeaderRightTitle, fontSize } = props;

  const [isChartInited, setIsChartInited] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const pieEchartContainerRef = useRef<HTMLElement>(null);

  const isNoData = useMemo(() => !Array.isArray(data?.data) || !data?.data?.length, [data?.data]);

  const { run: handleResizeRun } = useThrottleFn(() => {
    setContainerWidth(pieEchartContainerRef.current?.offsetWidth || 0);
  }, { leading: true, trailing: true });

  // 添加窗口缩放监听，获取容器width
  useEffect(() => {
    handleResizeRun();

    window?.addEventListener('resize', handleResizeRun);

    return () => {
      window?.removeEventListener('resize', handleResizeRun);
    };
  }, []);

  const { options: optionsOriginal } = useOptions({
    isCN,
    data,
    ignoreSortNames,
    seriesNameFormatter,
    tooltipPosition,
    containerWidth,
    tooltipHeaderLeftTitle: isCN ? data?.themeCN : data?.theme,
    tooltipHeaderRightTitle,
    fontSize,
  });

  const pieChartInstance = useRef<any>(null);
  const chartsInstance = useRef<IChartsIndexInstance>({
    getBarInstance: () => {},
    getLineInstance: () => {},
    getPieInstance: () => {
      return pieChartInstance.current;
    },
  });
  const {
    options,
    onHighlight,
    onDownplay,
  } = useOptionsExtend({
    loading: !isChartInited,
    chartType: ChartType.Pie,
    chartsInstance,
    options: optionsOriginal,
  });

  const handlePieChartMousemove = (event: any) => {
    event?.stopPropagation();
    event?.preventDefault();
    const chartInstance = pieChartInstance.current;
    chartInstance?.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: 0,
      position: (point: [number, number], params: any, dom: any, rect: any, size: any) => {
        const { contentSize } = size || {};
        const contentWidth = contentSize?.[0];
        return tooltipPosition === 'left' && event?.target?.clientWidth - event?.offsetX < contentWidth
          ? [event?.offsetX - 20 - contentWidth, event?.offsetY + 20]
          : [event?.offsetX + 20, event?.offsetY + 20];
      },
    });
    chartInstance?.getZr()?.setCursorStyle('pointer');
  };

  const handleChartInit = (chartInstance: any, chartDom: any) => {
    pieChartInstance.current = chartInstance;
    chartDom?.removeEventListener('mousemove', handlePieChartMousemove, false);
    chartDom?.addEventListener('mousemove', handlePieChartMousemove, false);
    /**
     * 这里因为BaseEchart组件内 dispose后 又会立即 init，从而导致isChartInit这个state的两次change仅触发一轮useEffect（监听isChartInit内的useEffect仅执行一次）
     * 因为isChartInit这个state在上面说的 dispose后又立即init的时候的快速change，会用最后一次change（即init引起的）触发useEffect
     * 从而导致usePieChartsHoverEffect hooks内，在dispose后又init的时候 没有走卸载组件instance的鼠标事件监听，然后又重新监听的流程，仍然是初始化的时候的那次监听
     * 但是由于dispose后，初始化的监听就监听不到了，所以需要 加setTimeout 来 让isChartInit触发两次useEffect，从而实现usePieChartsHoverEffect hooks内 走 销毁事件监听，重新监听事件的流程
     * 主要用途就是 保证usePieChartsHoverEffect hooks内的 事件监听生效
     */
    setTimeout(() => {
      setIsChartInited(true);
    });
  };

  useImperativeHandle(ref, () => ({
    onHighlight,
    onDownplay,
  }), [
    onHighlight,
    onDownplay,
  ]);

  return (
    <section ref={pieEchartContainerRef} className="dbi-pie-echart-box">
      {!isNoData && <BaseEchart echartsOptions={options} onInit={handleChartInit} />}
      {isNoData && <label className="dbi-pie-echart-box-no-data">{formatMessage({ id: 'common_nodata' })}</label>}
    </section>
  );
};

PieEchart.defaultProps = DEFAULT_PROPS;

export default memo(forwardRef(PieEchart), (prevProps: any, nextProps: any) => isEqual(prevProps, nextProps));
